/**
 * @file src/config/plans.js
 * @description Single source of truth for the subscription plan / tier taxonomy.
 *
 * Before this file existed, three incompatible vocabularies were hard-coded
 * across the UI:
 *   - PRICING.md / billing catalog : planId  = free | pro | max  (enterprise = archived)
 *   - foundation/users User model  : tier     = free | paid_individual | paid_org
 *   - feature-gate.element.jsx     : an INVENTED rank = free | silver | gold | enterprise
 *
 * The third one matched neither backend axis, so a paying `max` user resolved
 * to rank 0 and got locked OUT of features they had paid for.
 *
 * This module reconciles the two *legitimate* backend axes (planId and tier)
 * onto one ordered access scale, and accepts the legacy silver/gold/enterprise
 * words as aliases so no existing call site silently breaks.
 *
 * Keep aligned with:
 *   - PRICING.md (plan rows)
 *   - foundation/users User model (tier field)
 *   - foundation/subscriptions Install.js (plan seed)
 */

// ─── The one true access scale ────────────────────────────────────────────────
// Everything (planId, tier, legacy aliases) maps onto these ordered levels.
export const ACCESS = Object.freeze({ NONE: 0, INDIVIDUAL: 1, ORG: 2 });

/**
 * How many analysis areas a full report covers.
 *
 * The denominator behind every "N of 22 areas" on the site. It is the ceiling
 * of `features.report_depth.limit` in foundation/subscriptions Install.js —
 * free clips to 5, pro to 12, max to all 22 — and a plan's own limit always
 * comes from that plan, never from here. This is only the total they are a
 * fraction of.
 *
 * Lives here because the pricing page and the welcome card both need it, and
 * two hardcoded 22s that agree today are the defect this file exists to stop.
 */
export const REPORT_ANALYSIS_AREAS = 22;

// Billing plan ids (PRICING.md). `enterprise` is archived but still held by
// legacy subscribers → treat it as ORG-level, same as `max`.
export const PLAN_ACCESS = Object.freeze({
  free: ACCESS.NONE,
  pro: ACCESS.INDIVIDUAL,
  max: ACCESS.ORG,
  enterprise: ACCESS.ORG,
});

export const PLAN_LABELS = Object.freeze({
  free: 'Free',
  pro: 'Pro',
  max: 'Max',
  enterprise: 'Enterprise',
});

// ── Upgrade-card highlights ──────────────────────────────────────────
//
// ⚠️ THESE ARE DERIVED, NOT WRITTEN. Here is why.
//
// This was a hand-typed array, and it was the THIRD copy of the same facts
// (the plan rows in foundation/subscriptions/src/lifecycle/Install.js, the
// public /pricing page content, and this). Three hand-maintained copies of
// one truth drift, and this one had:
//
//   "500 pillars / month"      — internal Newton accounting. A customer
//                                does not buy pillars; the public pricing
//                                page deliberately never says the word.
//   "Up to 3 concurrent runs"  — NOT A REAL LIMIT. There is no
//                                `concurrent_runs` feature key and nothing
//                                enforces one anywhere in the codebase. The
//                                app was advertising a constraint the
//                                platform does not have.
//
// while omitting every axis a customer actually chooses on: how many
// projects, how deep the report goes, which models, seats.
//
// So the bullets are now COMPUTED from a plan's own `features` — the same
// record the platform enforces against — using the vocabulary the public
// /pricing page already settled on. That page states the rule this file now
// keeps: "Every row is a limit the platform enforces, not a description of
// one." A limit cannot be advertised here unless it exists there.
//
// NOT included, because no entitlement backs them: "revisions" and
// "instant customer support". Both are real product ideas and neither is a
// feature key, a limit, or a gate anywhere in the system — writing them in
// would repeat exactly the mistake `concurrent runs` was.

const UNLIMITED = -1;
const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : null);

/** `-1` means unlimited in the plan schema; 0/absent means not included. */
const limitOf = (plan, key) => num(plan?.features?.[key]?.limit);
const enabledOf = (plan, key) => plan?.features?.[key]?.enabled === true;
const countLabel = (n, one, many) =>
  n === UNLIMITED ? `Unlimited ${many}` : `${n} ${n === 1 ? one : many}`;

// The customer-facing name for a model tier. The plan carries raw model ids
// (`gpt-4o`, `claude-3-5-sonnet`); a customer buys "the better model", not a
// vendor SKU, and naming third-party models in-product would also date the
// copy every time one is swapped.
const modelLabel = (plan) => {
  const n = (plan?.aiEntitlements?.allowedModels || []).length;
  if (n >= 4) return 'Most capable Moksy models';
  if (n >= 3) return 'Advanced Moksy models';
  return 'Standard Moksy models';
};

/**
 * Turn a plan RECORD (as served by GET /billing/plans) into the bullets
 * shown on an upgrade card. Order is by what a customer decides on first.
 */
export function planHighlights(plan) {
  if (!plan?.features) return [];
  const out = [];

  const projects = limitOf(plan, 'projects');
  if (projects !== null) out.push(countLabel(projects, 'project', 'projects'));

  const depth = limitOf(plan, 'report_depth');
  if (depth !== null) {
    // GC-038: these were three hand-typed 22s, in the file whose own header
    // says two hardcoded numbers that agree today are the defect it exists to
    // stop. Read the constant twenty lines up instead.
    out.push(depth >= REPORT_ANALYSIS_AREAS
      ? `Complete report — all ${REPORT_ANALYSIS_AREAS} analysis areas`
      : `Fuller report — ${depth} of ${REPORT_ANALYSIS_AREAS} analysis areas`);
  }

  out.push(modelLabel(plan));

  if (enabledOf(plan, 'review_submit')) {
    out.push(limitOf(plan, 'review_submit') === UNLIMITED
      ? 'Unlimited expert review requests'
      : 'Expert review requests');
  }

  const seats = limitOf(plan, 'team_members');
  if (seats !== null && seats !== 1) out.push(countLabel(seats, 'seat', 'seats'));

  if (enabledOf(plan, 'review_eligible')) out.push('Eligible to become a paid reviewer');

  return out;
}

// Fallback for the moment before GET /billing/plans answers. Every line is
// the output of `planHighlights` against the seeded plan, and a test holds
// it to that — so a limit changed in the subscriptions service fails here
// rather than leaving the app quietly advertising the old number.
export const PLAN_HIGHLIGHTS = Object.freeze({
  pro: [
    '25 projects',
    'Fuller report — 12 of 22 analysis areas',
    'Advanced Moksy models',
    'Unlimited expert review requests',
    '5 seats',
  ],
  max: [
    'Unlimited projects',
    'Complete report — all 22 analysis areas',
    'Most capable Moksy models',
    'Unlimited expert review requests',
    'Unlimited seats',
    'Eligible to become a paid reviewer',
  ],
});

// Customer state-machine tiers (foundation/users User model).
export const TIER_ACCESS = Object.freeze({
  free: ACCESS.NONE,
  paid_individual: ACCESS.INDIVIDUAL,
  paid_org: ACCESS.ORG,
});

// Presentational labels for the tier axis. `paid_org` reads as "Team" so it
// maps to a name a customer recognises rather than the internal token.
export const TIER_LABELS = Object.freeze({
  free: 'Free',
  paid_individual: 'Pro',
  paid_org: 'Team',
  anonymous: 'Guest',
  guest: 'Guest',
});

// Legacy / loose requirement words we still accept as gate inputs so old call
// sites keep working while they migrate to real plan ids or tiers.
const REQUIREMENT_ALIASES = Object.freeze({
  silver: 'pro',
  gold: 'max',
  enterprise: 'max',
  paid: 'paid_individual',
  paid_any: 'paid_individual',
});

const norm = (v) => String(v == null ? '' : v).toLowerCase().trim();

/**
 * The user's effective access level, derived from BOTH backend axes. Using the
 * max of the two means a stale/missing field on one axis can't wrongly lock a
 * paying user out (e.g. planId='max' but tier not yet propagated).
 */
export function accessLevel(user) {
  const sub = user?.subscription || {};
  const fromPlan = PLAN_ACCESS[norm(sub.planId ?? user?.planTier)] ?? ACCESS.NONE;
  const fromTier = TIER_ACCESS[norm(sub.tier)] ?? ACCESS.NONE;
  return Math.max(fromPlan, fromTier);
}

/**
 * The access level a requirement demands. Accepts a plan id (`pro`/`max`), a
 * tier (`paid_individual`/`paid_org`), or a legacy alias (`silver`/`gold`/…).
 * An unknown-but-truthy requirement means "any paid plan" → INDIVIDUAL.
 */
export function requiredAccess(requirement) {
  const raw = norm(requirement);
  if (!raw || raw === 'free') return ACCESS.NONE;
  const key = REQUIREMENT_ALIASES[raw] || raw;
  if (key in PLAN_ACCESS) return PLAN_ACCESS[key];
  if (key in TIER_ACCESS) return TIER_ACCESS[key];
  return ACCESS.INDIVIDUAL;
}

/** True iff `user` satisfies `requirement` (plan id, tier, or alias). */
export function meetsRequirement(user, requirement) {
  return accessLevel(user) >= requiredAccess(requirement);
}

/** True iff the user is on any paid plan/tier. */
export function isPaid(user) {
  return accessLevel(user) >= ACCESS.INDIVIDUAL;
}

/** Human label for a tier token (badge/pill copy). */
export function tierLabel(tier) {
  const key = norm(tier);
  return TIER_LABELS[key] || (key ? key : 'Free');
}

/** Human label for a required plan/tier/alias, for upgrade-prompt copy. */
export function requirementLabel(requirement) {
  const level = requiredAccess(requirement);
  if (level >= ACCESS.ORG) return PLAN_LABELS.max;
  if (level >= ACCESS.INDIVIDUAL) return PLAN_LABELS.pro;
  return PLAN_LABELS.free;
}

/** Upgrade-card highlights for a required plan/tier/alias. */
export function requirementHighlights(requirement) {
  const level = requiredAccess(requirement);
  return level >= ACCESS.ORG ? PLAN_HIGHLIGHTS.max : PLAN_HIGHLIGHTS.pro;
}
